/**
 * Live-match arena geometry — AUTO-DERIVED from content (#331).
 *
 * ROOT CAUSE (#331): the live `MatchScreen` was wired to `fixtures/
 * snapshots.ts`'s `FIXTURE_ARENA` — a demo/visual-baseline fixture whose
 * `pillars: []` predates #259 shipping the `bare` map's 4 real pillars.
 * `FIXTURE_ARENA` is DELIBERATELY pillar-less (its own comment: the pillar
 * render path is exercised by synthetic-pillar unit tests instead, so the
 * Playwright baseline in `demo.ts`/`demo.html` never has to be re-captured
 * when map content changes) — it was simply the WRONG data source for a
 * live match, not itself wrong. The render code (`drawArena` + the #243 3D
 * composite; the pillars themselves are 3D props since #816/#829) was correct
 * the whole time; every real match just fed it an empty `pillars` array, so
 * pillars never drew despite sim-side collision (which reads the real map
 * content) being fully correct.
 *
 * SOURCE: read at BUILD TIME from the on-disk map JSON (`packages/content/
 * data/maps/*.json`) via `import.meta.glob` — the same glob-bridge idiom
 * `champions/catalog.ts` (#239) and `arenaAssets.ts` (#172) use so this
 * never pulls `@pit/content`'s runtime loader (`node:crypto`) into the
 * browser bundle. Under vitest the literal glob still resolves the real
 * committed JSON (no GPU / build step needed to test it).
 *
 * `LIVE_ARENA` (`bare`) is the pre-match/no-`mapSlug`-yet fallback ONLY.
 * #700 shipped the actual per-match resolution: the wire's `matchCreated`/
 * `matchPhase` `mapSlug` field now carries whichever map the room's
 * `createMatch` actually resolved to (`bare`, `nagrand`, or any future
 * addition) — `MatchScreen.tsx` looks it up via `arenaForSlug` below instead
 * of always rendering `LIVE_ARENA`.
 */

import { z } from "zod";
import type { ArenaGeometry } from "./stage.js";

/** Minimal on-disk map shape this catalog needs — mirrors `catalog.ts`'s
 *  `CHAMPION_META_SCHEMA` pattern: a permissive `z.object` (not
 *  `strictObject`) so it only reads the render-relevant fields and never
 *  chokes on unrelated map fields (`schemaVersion`, `id`) it doesn't need. */
const MAP_META_SCHEMA = z.object({
  slug: z.string().min(1),
  width: z.number(),
  height: z.number(),
  // #235: `spawns[team]` is an array of spawn points (3 per side at MVP
  // content), not a single point — mirrors `PitMap.spawns`'s shape.
  spawns: z.array(z.array(z.object({ x: z.number(), y: z.number() })).min(1)).min(1),
  pillars: z.array(z.object({ x: z.number(), y: z.number(), radius: z.number() })),
});

/** A content map's render-relevant geometry, keyed by its human slug. */
interface CatalogMap extends ArenaGeometry {
  readonly slug: string;
}

/**
 * Build-time map of every on-disk map JSON → its RAW text, then parsed down
 * to `ArenaGeometry` + `slug`. Same literal-`import.meta.glob` rule as
 * `catalog.ts` / `arenaAssets.ts`: the call MUST stay in this literal form
 * for Vite's static transform to pick it up.
 */
function loadContentMaps(): readonly CatalogMap[] {
  const modules = import.meta.glob<string>(
    "../../../../packages/content/data/maps/*.json",
    { eager: true, query: "?raw", import: "default" },
  );
  const out: CatalogMap[] = [];
  for (const path of Object.keys(modules).sort()) {
    const raw = modules[path];
    if (raw === undefined) continue;
    const meta = MAP_META_SCHEMA.parse(JSON.parse(raw) as unknown);
    out.push({
      slug: meta.slug,
      width: meta.width,
      height: meta.height,
      spawns: meta.spawns,
      pillars: meta.pillars,
    });
  }
  return out;
}

/** Every map shipped in content (render-relevant fields only). */
export const ALL_CONTENT_MAPS: readonly CatalogMap[] = loadContentMaps();

/**
 * The slug of the one map the only wired mode runs on (`apps/server/src/
 * modes.ts`'s `1v1`/`3v3` entries: `mapId: "map_3jY2xYPXe7"`, slug `bare`).
 * Exported (#678) as `MapPicker`'s default selection — it's the server's
 * own configured default map, so defaulting the picker to it and always
 * sending `mapSlug` is behaviorally identical to omitting the field.
 */
export const LIVE_MAP_SLUG = "nagrand";

/** Degenerate empty-arena fallback — never thrown, mirrors every other
 *  content-glob loader's rip-out contract (missing content → safest
 *  fallback, not a crash). Should not happen: `bare` ships in every
 *  checkout and a content test asserts it stays findable. */
const FALLBACK_ARENA: ArenaGeometry = { width: 4000, height: 4000, spawns: [], pillars: [] };

/**
 * Live-match arena geometry (#331) — the real `bare` map's dimensions,
 * spawns, AND pillars, replacing the old (wrong) `FIXTURE_ARENA` reuse in
 * `MatchScreen.tsx`. `fixtures/snapshots.ts`'s `FIXTURE_ARENA` stays exactly
 * as-is; it is test/demo-only.
 */
export const LIVE_ARENA: ArenaGeometry = (() => {
  const nagrand = ALL_CONTENT_MAPS.find((m) => m.slug === LIVE_MAP_SLUG);
  if (nagrand === undefined) return FALLBACK_ARENA;
  return {
    width: nagrand.width,
    height: nagrand.height,
    spawns: nagrand.spawns,
    pillars: nagrand.pillars,
    // The map's identity, carried alongside its geometry (#628).
    slug: nagrand.slug,
  };
})();

/**
 * #700: resolve the arena geometry for a match's ACTUAL map slug (the wire
 * `matchCreated`/`matchPhase` field, `state.mapSlug`) — `MatchScreen.tsx`'s
 * one call site, replacing the old hardcoded `LIVE_ARENA` (`bare`) render
 * regardless of which map the room was actually created with. Falls back to
 * `LIVE_ARENA` when `slug` is `null` (no `matchCreated`/`matchPhase` has
 * arrived yet — e.g. the pre-match window, or a legacy/demo path that never
 * sends either) or references a slug absent from `ALL_CONTENT_MAPS` (should
 * not happen: the server only ever resolves a slug that exists in the SAME
 * content bundle the client built this catalog from).
 */
export function arenaForSlug(slug: string | null): ArenaGeometry {
  if (slug === null) return LIVE_ARENA;
  const map = ALL_CONTENT_MAPS.find((m) => m.slug === slug);
  if (map === undefined) return LIVE_ARENA;
  return { width: map.width, height: map.height, spawns: map.spawns, pillars: map.pillars, slug: map.slug };
}
